import { HiMenuAlt3 } from "react-icons/hi";
import DashIcon from "components/icons/DashIcon";
import routes from "routes";
import { useState, useEffect } from "react";
import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useRecoilValue } from "recoil";
import { openAddUpdatePatientModalOthersState } from "store/patientModalState";
import "./styles.css";

type RouteItem = {
  name: string;
  layout: string;
  path: string;
  icon?: ReactNode;
  component?: ReactNode;
  margin?: boolean;
};

const Sidebar = (_props: Record<string, unknown>) => {
  const [opens, setOpens] = useState(() => {
    const savedState = localStorage.getItem("sidebarOpen");
    return savedState === "true";
  });

  useEffect(() => {
    localStorage.setItem("sidebarOpen", String(opens));
  }, [opens]);

  const location = useLocation();

  const activeRoute = (routeName: string) =>
    location.pathname.includes(routeName);

  const [route] = useState<RouteItem[]>(routes as RouteItem[]);

  const patientModalOthers = useRecoilValue(openAddUpdatePatientModalOthersState);

  const handleMouseEnter = () => setOpens(true);
  const handleMouseLeave = () => setOpens(false);

  const containerVariants = {
    hidden: { opacity: 1 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: "30px" },
    show: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.2,
      },
    },
  };

  return (
    <div
      className={`relative flex min-h-screen shrink-0 ${
        opens
          ? "w-[265px] transition-[width] duration-500"
          : "w-16 transition-[width] duration-500"
      }`}
    >
      <div
        className={`sidebar fixed top-0 left-0 z-50 h-full px-3 text-clinic-300 transition-all duration-500 ease-in-out ${
          opens ? "w-[265px]" : "w-16"
        }`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div
          className={`flex items-center py-3 ${
            opens ? "justify-between" : "justify-center"
          }`}
        >
          {opens && (
            <div className="ml-2 flex min-w-0 flex-col leading-tight">
              <span className="font-poppins text-[17px] font-bold tracking-wide text-white">
                Babynama
              </span>
              <span className="font-mono text-[9px] font-semibold uppercase tracking-[0.22em] text-clinic-500">
                Clinic-OS
              </span>
            </div>
          )}
          {/* was a bare clickable icon: no button, no name, no keyboard, no focus ring */}
          <button
            type="button"
            aria-label={opens ? "Collapse sidebar" : "Expand sidebar"}
            aria-expanded={opens}
            onClick={() => setOpens(!opens)}
            className="touch-manipulation rounded-md p-1 text-clinic-400 hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action-400"
          >
            <HiMenuAlt3 size={22} aria-hidden="true" />
          </button>
        </div>

        <motion.nav
          aria-label="Main"
          className={`relative flex h-[calc(100vh-4rem)] flex-col gap-2 overflow-y-scroll ${
            !opens ? "mt-4" : "links-container pb-6"
          }`}
          style={!opens ? { scrollbarWidth: "none", msOverflowStyle: "none" }:{}} // Hide scrollbar for Firefox and IE/Edge
          initial="hidden"
          animate={opens ? "show" : "hidden"}
          variants={containerVariants}
        >
            <style {...({ jsx: true } as Record<string, unknown>)}>{`
              ::-webkit-scrollbar {
                width: 0;
                height: 0;
              }
            `}</style>

          {route
            .filter(
              (r: RouteItem) =>
                r.layout === "" ||
                r.layout === "/auth" ||
                r.layout === "/rtl"
            )
            .map(
              (menu: RouteItem, index: number) =>
                menu.path !== "sign-in" && (
                  <Link
                    to={`${menu.layout}/${menu.path}`}
                    key={index}
                    aria-current={activeRoute(menu.path) ? "page" : undefined}
                    className={`${
                      menu.margin && "mt-5"
                    } group relative flex items-center gap-3.5 rounded-md p-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action-400 ${
                      activeRoute(menu.path)
                        ? "bg-white/10 text-white"
                        : "text-clinic-400 hover:bg-white/5 hover:text-white"
                    }`}
                  >
                    {/* the unmistakable current-item mark: a teal bar on the item's
                        edge (left-0 — anything further left is clipped by the
                        nav's overflow-y-scroll box) */}
                    {activeRoute(menu.path) && (
                      <span
                        aria-hidden="true"
                        className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-action-400"
                      />
                    )}
                    <span
                      aria-hidden="true"
                      className={
                        activeRoute(menu.path)
                          ? "text-action-300"
                          : "text-clinic-500 group-hover:text-clinic-300"
                      }
                    >
                      {menu.icon ? menu.icon : <DashIcon />}
                    </span>

                    {/* were <h2>s — nav links are not headings, and they were announced twice */}
                    <motion.span
                      className={`whitespace-pre font-[500] ${
                        activeRoute(menu.path) ? "text-white" : "text-clinic-300"
                      }`}
                      variants={itemVariants}
                    >
                      {menu.name}
                    </motion.span>

                    {/* collapsed-rail hover tooltip — purely visual, the link is already named */}
                    <span
                      aria-hidden="true"
                      className={`${
                        patientModalOthers.open && "hidden"
                      } absolute left-48 z-10 w-0 overflow-hidden whitespace-pre rounded-md bg-clinic-800 px-0 py-0 text-xs font-semibold text-white shadow-lg ring-1 ring-white/10 transition-all ease-in-out group-hover:left-14 group-hover:w-fit group-hover:px-2 group-hover:py-1 group-hover:duration-300 motion-reduce:transition-none`}
                    >
                      {menu.name}
                    </span>
                  </Link>
                )
            )}
        </motion.nav>
      </div>

      {/* <div className="ml-16 flex-1 p-4">
        Main content goes here
      </div> */}
    </div>
  );
};

export default Sidebar;
